import { create } from 'zustand';
import type { Model3D, Category, Favorite, UserModel } from '@visionar/shared';
import { modelsApi, categoriesApi, favoritesApi, userModelsApi } from '@/lib/api-client';

interface ModelsState {
  // Data
  models: Model3D[];
  featuredModels: Model3D[];
  categories: Category[];
  favorites: Favorite[];
  userModels: UserModel[];
  publicModels: UserModel[];

  // Loading states
  isLoadingModels: boolean;
  isLoadingFeatured: boolean;
  isLoadingCategories: boolean;
  isLoadingFavorites: boolean;
  isLoadingUserModels: boolean;
  isLoadingPublicModels: boolean;
  isUploadingModel: boolean;

  // Pagination
  currentPage: number;
  totalPages: number;
  hasMore: boolean;

  // Filters
  selectedCategory: string | null;
  searchQuery: string;

  // Actions
  fetchModels: (page?: number) => Promise<void>;
  fetchFeatured: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchFavorites: () => Promise<void>;
  searchModels: (query: string) => Promise<void>;
  setSelectedCategory: (category: string | null) => void;
  toggleFavorite: (modelId: string) => Promise<void>;
  isFavorite: (modelId: string) => boolean;
  loadMore: () => Promise<void>;
  reset: () => void;

  // User Models Actions
  fetchUserModels: () => Promise<void>;
  fetchPublicModels: () => Promise<void>;
  uploadModel: (file: File, name: string, description?: string, isPublic?: boolean) => Promise<UserModel>;
  updateUserModel: (id: string, updates: { name?: string; description?: string | null; isPublic?: boolean }) => Promise<void>;
  deleteUserModel: (id: string) => Promise<void>;
}

export const useModelsStore = create<ModelsState>((set, get) => ({
  // Data
  models: [],
  featuredModels: [],
  categories: [],
  favorites: [],
  userModels: [],
  publicModels: [],

  // Loading states
  isLoadingModels: false,
  isLoadingFeatured: false,
  isLoadingCategories: false,
  isLoadingFavorites: false,
  isLoadingUserModels: false,
  isLoadingPublicModels: false,
  isUploadingModel: false,

  // Pagination
  currentPage: 1,
  totalPages: 1,
  hasMore: false,

  // Filters
  selectedCategory: null,
  searchQuery: '',

  // Actions
  fetchModels: async (page = 1) => {
    const { selectedCategory } = get();
    set({ isLoadingModels: true });

    try {
      const params: Record<string, unknown> = { page, limit: 20 };
      if (selectedCategory) {
        params.category = selectedCategory;
      }

      const response = await modelsApi.list(params as Parameters<typeof modelsApi.list>[0]);

      set({
        models: page === 1 ? response.data : [...get().models, ...response.data],
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.page < response.pagination.totalPages,
        isLoadingModels: false,
      });
    } catch (error) {
      set({ isLoadingModels: false });
      throw error;
    }
  },

  fetchFeatured: async () => {
    set({ isLoadingFeatured: true });
    try {
      const response = await modelsApi.featured();
      set({ featuredModels: response.data, isLoadingFeatured: false });
    } catch (error) {
      set({ isLoadingFeatured: false });
      throw error;
    }
  },

  fetchCategories: async () => {
    set({ isLoadingCategories: true });
    try {
      const response = await categoriesApi.list();
      set({ categories: response.data, isLoadingCategories: false });
    } catch (error) {
      set({ isLoadingCategories: false });
      throw error;
    }
  },

  fetchFavorites: async () => {
    set({ isLoadingFavorites: true });
    try {
      const response = await favoritesApi.list();
      set({ favorites: response.data, isLoadingFavorites: false });
    } catch (error) {
      set({ isLoadingFavorites: false });
      throw error;
    }
  },

  searchModels: async (query: string) => {
    set({ searchQuery: query, isLoadingModels: true });

    if (!query.trim()) {
      set({ isLoadingModels: false });
      return get().fetchModels(1);
    }

    try {
      const response = await modelsApi.search(query);
      set({
        models: response.data,
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.page < response.pagination.totalPages,
        isLoadingModels: false,
      });
    } catch (error) {
      set({ isLoadingModels: false });
      throw error;
    }
  },

  setSelectedCategory: (category) => {
    set({ selectedCategory: category, models: [], currentPage: 1 });
    get().fetchModels(1);
  },

  toggleFavorite: async (modelId: string) => {
    const { favorites } = get();
    const isFav = favorites.some((f) => f.modelId === modelId);

    try {
      if (isFav) {
        await favoritesApi.remove(modelId);
        set({ favorites: favorites.filter((f) => f.modelId !== modelId) });
      } else {
        const response = await favoritesApi.add(modelId);
        set({ favorites: [...favorites, response.data] });
      }
    } catch (error) {
      // Revert on error - refetch favorites
      get().fetchFavorites();
      throw error;
    }
  },

  isFavorite: (modelId: string) => {
    return get().favorites.some((f) => f.modelId === modelId);
  },

  loadMore: async () => {
    const { currentPage, hasMore, isLoadingModels } = get();
    if (!hasMore || isLoadingModels) return;
    await get().fetchModels(currentPage + 1);
  },

  reset: () => {
    set({
      models: [],
      selectedCategory: null,
      searchQuery: '',
      currentPage: 1,
      totalPages: 1,
      hasMore: false,
    });
  },

  // User Models Actions
  fetchUserModels: async () => {
    set({ isLoadingUserModels: true });
    try {
      const response = await userModelsApi.getMyModels();
      set({ userModels: response.data, isLoadingUserModels: false });
    } catch (error) {
      set({ isLoadingUserModels: false });
      throw error;
    }
  },

  fetchPublicModels: async () => {
    set({ isLoadingPublicModels: true });
    try {
      const response = await userModelsApi.getPublicModels();
      set({ publicModels: response.data, isLoadingPublicModels: false });
    } catch (error) {
      set({ isLoadingPublicModels: false });
      throw error;
    }
  },

  uploadModel: async (file: File, name: string, description?: string, isPublic = false) => {
    set({ isUploadingModel: true });
    try {
      const response = await userModelsApi.upload(file, name, description, isPublic);
      const newModel = response.data;

      // Add to local state
      set((state) => ({
        userModels: [newModel, ...state.userModels],
        isUploadingModel: false,
      }));

      return newModel;
    } catch (error) {
      set({ isUploadingModel: false });
      throw error;
    }
  },

  updateUserModel: async (id: string, updates) => {
    try {
      const response = await userModelsApi.updateModel(id, updates);
      const updatedModel = response.data;

      // Update in local state
      set((state) => ({
        userModels: state.userModels.map((m) => (m.id === id ? updatedModel : m)),
      }));
    } catch (error) {
      throw error;
    }
  },

  deleteUserModel: async (id: string) => {
    try {
      await userModelsApi.deleteModel(id);

      // Remove from local state
      set((state) => ({
        userModels: state.userModels.filter((m) => m.id !== id),
      }));
    } catch (error) {
      throw error;
    }
  },
}));
